import { useEffect, useState } from "react";
import api, { moneyExact } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Modal } from "./Projects";
import { LineItemsEditor, blankLine } from "./Estimates";
import { Plus, Trash2, Receipt, Wallet, Pencil } from "lucide-react";

function statusBadge(status) {
  if (status === "paid") return "badge-green";
  if (status === "partial") return "badge-rust"; // orange/rust accent for partial
  if (status === "pending") return "";
  return "badge-rust";
}

export default function AdminInvoices() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editDeposit, setEditDeposit] = useState(null); // {invoice, value}

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const save = async () => {
    if (!editing.client_id) return alert("Select a client");
    if (editing.line_items.length === 0) return alert("Add at least one line item");
    const deposit = editing.deposit_amount === "" ? null : Number(editing.deposit_amount);
    await api.post("/invoices", {
      client_id: editing.client_id,
      project_id: null,
      title: editing.title,
      line_items: editing.line_items,
      notes: editing.notes,
      tax_rate: editing.tax_rate,
      due_date: editing.due_date || null,
      deposit_amount: deposit,
    });
    setEditing(null);
    load();
  };

  const saveDeposit = async () => {
    const v = Number(editDeposit.value || 0);
    await api.patch(`/invoices/${editDeposit.invoice.id}`, { deposit_amount: v });
    setEditDeposit(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    load();
  };

  return (
    <DashboardLayout
      role="admin" title="Invoices"
      subtitle="Bill clients, request a deposit, and accept Stripe payments online."
      actions={<button className="btn-primary" onClick={() => setEditing({
        client_id: "", title: "", line_items: [{ ...blankLine }], notes: "", tax_rate: 0.0825, due_date: "", deposit_amount: "",
      })} data-testid="new-invoice-btn"><Plus size={16}/> New Invoice</button>}
    >
      {items.length === 0 ? (
        <div className="card p-12 text-center text-sm" style={{color:'var(--text-muted)'}}>
          <Receipt className="mx-auto mb-3"/>
          No invoices yet.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="text-xs uppercase tracking-wider" style={{color:'var(--text-muted)'}}>
              <tr className="border-b border-white/5">
                <th className="text-left p-4">Title</th>
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Total</th>
                <th className="text-right p-4">Paid</th>
                <th className="text-right p-4">Deposit</th>
                <th className="text-right p-4">Due</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {items.map((e) => {
                const client = clients.find((c) => c.id === e.client_id);
                const paid = Number(e.amount_paid || 0);
                const balance = Math.max(0, Number(e.total) - paid);
                return (
                  <tr key={e.id} className="border-b border-white/5">
                    <td className="p-4 font-medium">{e.title}</td>
                    <td className="p-4">{client?.name || <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                    <td className="p-4">
                      <span className={`badge ${statusBadge(e.status)}`}>{e.status}</span>
                    </td>
                    <td className="p-4 text-right font-semibold">{moneyExact(e.total)}</td>
                    <td className="p-4 text-right">
                      <div className="font-semibold" style={{color: paid > 0 ? '#86efac' : 'var(--text-muted)'}}>{moneyExact(paid)}</div>
                      {balance > 0 && paid > 0 && <div className="text-[10px] uppercase tracking-wider" style={{color:'var(--text-muted)'}}>{moneyExact(balance)} left</div>}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => setEditDeposit({ invoice: e, value: e.deposit_amount || 0 })}
                        className="inline-flex items-center gap-1.5 hover:underline"
                        style={{color: e.deposit_amount > 0 ? 'var(--accent)' : 'var(--text-muted)'}}
                        data-testid={`edit-deposit-${e.id}`}
                        title="Set deposit amount"
                      >
                        {e.deposit_amount > 0 ? moneyExact(e.deposit_amount) : "Set"}
                        <Pencil size={11}/>
                      </button>
                    </td>
                    <td className="p-4 text-right text-xs" style={{color:'var(--text-muted)'}}>{e.due_date || '—'}</td>
                    <td className="p-4 text-right">
                      <button className="text-red-400 hover:text-red-300" onClick={() => remove(e.id)} data-testid={`delete-inv-${e.id}`}><Trash2 size={14}/></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create-invoice modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)} title="New Invoice" size="6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Client</div>
              <select className="input" value={editing.client_id} onChange={(e) => setEditing({...editing, client_id: e.target.value})} data-testid="inv-client">
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            </label>
            <label className="block md:col-span-2">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Title</div>
              <input className="input" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} data-testid="inv-title"/>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Due date</div>
              <input type="date" className="input" value={editing.due_date} onChange={(e) => setEditing({...editing, due_date: e.target.value})}/>
            </label>
          </div>
          <LineItemsEditor
            items={editing.line_items}
            setItems={(li) => setEditing({...editing, line_items: li})}
            taxRate={editing.tax_rate}
            setTaxRate={(r) => setEditing({...editing, tax_rate: r})}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold flex items-center gap-2" style={{color:'var(--text-muted)'}}>
                <Wallet size={12}/> Deposit Required <span className="opacity-50">(optional)</span>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{color:'var(--text-muted)'}}>$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input !pl-7"
                  placeholder="e.g. 2500.00"
                  value={editing.deposit_amount}
                  onChange={(e) => setEditing({...editing, deposit_amount: e.target.value})}
                  data-testid="inv-deposit"
                />
              </div>
              <div className="text-[11px] mt-1.5" style={{color:'var(--text-muted)'}}>
                Show your client a "Pay Deposit" button for this exact amount. Leave blank or 0 to skip.
              </div>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Notes</div>
              <textarea className="input" rows={3} value={editing.notes} onChange={(e) => setEditing({...editing, notes: e.target.value})}/>
            </label>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="inv-save">Create Invoice</button>
          </div>
        </Modal>
      )}

      {/* Inline-edit deposit modal */}
      {editDeposit && (
        <Modal onClose={() => setEditDeposit(null)} title="Set Deposit Amount">
          <div className="space-y-4">
            <div className="text-sm" style={{color:'var(--text-muted)'}}>
              Invoice total: <strong style={{color:'var(--text)'}}>{moneyExact(editDeposit.invoice.total)}</strong>
              <br/>Already paid: <strong style={{color:'var(--text)'}}>{moneyExact(editDeposit.invoice.amount_paid || 0)}</strong>
            </div>
            <label className="block">
              <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{color:'var(--text-muted)'}}>Deposit Required</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{color:'var(--text-muted)'}}>$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={editDeposit.invoice.total}
                  className="input !pl-7"
                  value={editDeposit.value}
                  onChange={(e) => setEditDeposit({...editDeposit, value: e.target.value})}
                  data-testid="edit-deposit-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 mt-2">
                {[25, 33, 50].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setEditDeposit({ ...editDeposit, value: (editDeposit.invoice.total * pct / 100).toFixed(2) })}
                    className="btn-ghost !py-1 !px-3 !text-xs"
                  >
                    {pct}%
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setEditDeposit({ ...editDeposit, value: 0 })}
                  className="btn-ghost !py-1 !px-3 !text-xs"
                >
                  Clear
                </button>
              </div>
            </label>
            <div className="flex gap-3 justify-end">
              <button className="btn-ghost" onClick={() => setEditDeposit(null)}>Cancel</button>
              <button className="btn-primary" onClick={saveDeposit} data-testid="save-deposit">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </DashboardLayout>
  );
}
